import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PersonRef } from '@/features/catalog/api'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Prestadores da clínica (docs/api/admin.md, "Prestadores"; RN-ADM-20 a 24). Quem responde
 * tecnicamente pelo procedimento: nome, conselho, profissão e registro entram no documento assinado.
 */

/** Uma linha da tabela conselho × profissão do sistema (`GET /admin/professional-councils`). */
export interface ProfessionalCouncil {
  code: string
  name: string
  /** O exemplo de registro do conselho ("SP 123456"); `null` quando não há registro. */
  registrationExample: string | null
  requiresRegistration: boolean
  professions: { code: string; name: string }[]
}

export interface Practitioner {
  id: string
  /** Guardado como digitado; a interface exibe em caixa de título. */
  name: string
  council: string
  councilName: string
  profession: string
  professionName: string
  specialty: string | null
  /** "SP 897886"; `null` para quem não tem conselho. */
  registrationNumber: string | null
  /** "CRM-SP 897886": o que o documento imprime. */
  fullRegistration: string | null
  /** O usuário ativo que entra no sistema como esta prestadora, quando existe. */
  user: { id: string; name: string } | null
  archivedAt: string | null
  archivedBy: PersonRef | null
  createdAt: string
  updatedAt: string
}

export interface PractitionerInput {
  name: string
  council: string
  profession: string
  specialty: string | null
  registrationNumber: string | null
}

export type PractitionerFilter = 'active' | 'archived'

export interface PractitionerListMeta extends ListMeta {
  counts: Record<PractitionerFilter, number>
}

export interface PractitionerUsage {
  contractCount: number
  treatmentPlanCount: number
  activeUser: { id: string; name: string } | null
  lastUsedAt: string | null
}

export const practitionerKeys = {
  all: ['practitioners'] as const,
  list: (params: object) => ['practitioners', 'list', params] as const,
  detail: (id: string) => ['practitioners', 'detail', id] as const,
  usage: (id: string | null) => ['practitioners', 'usage', id] as const,
  // Fora de `all`: a tabela é do sistema e não muda quando um cadastro muda.
  councils: ['professional-councils'] as const,
}

/** A tabela conselho × profissão: dado do sistema, lido uma vez por sessão (P-ADM-01). */
export function useProfessionalCouncils() {
  const api = useApi()
  return useQuery({
    queryKey: practitionerKeys.councils,
    queryFn: () => api.get<ProfessionalCouncil[]>('/admin/professional-councils'),
    staleTime: Infinity,
  })
}

export function usePractitioners(params: {
  filter: PractitionerFilter
  search?: string
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: practitionerKeys.list(params),
    queryFn: () =>
      api.list<Practitioner, PractitionerListMeta>('/admin/practitioners', {
        filter: params.filter,
        search: params.search,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

export function usePractitioner(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: practitionerKeys.detail(id ?? ''),
    queryFn: () => api.get<Practitioner>(`/admin/practitioners/${id}`),
    enabled: Boolean(id),
  })
}

export function useSavePractitioner(id?: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: PractitionerInput) =>
      id
        ? api.put<Practitioner>(`/admin/practitioners/${id}`, input)
        : api.post<Practitioner>('/admin/practitioners', input, {
            idempotencyKey: idempotency.keyFor(input),
          }),
    onSuccess: () => {
      idempotency.reset()
      return queryClient.invalidateQueries({ queryKey: practitionerKeys.all })
    },
  })
}

export function usePractitionerAction() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'archive' | 'restore' }) =>
      api.post<Practitioner>(`/admin/practitioners/${id}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: practitionerKeys.all }),
  })
}

/** Os números do modal de arquivar (RN-ADM-23). Só é pedido quando o modal abre. */
export function usePractitionerUsage(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: practitionerKeys.usage(id),
    queryFn: () => api.get<PractitionerUsage>(`/admin/practitioners/${id}/usage`),
    enabled: Boolean(id),
  })
}
